import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import Typography from "@insite/mobius/Typography";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import LazyImage from "@insite/mobius/LazyImage";
import GridItem from "@insite/mobius/GridItem";
import GridContainer from "@insite/mobius/GridContainer";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Link from "@insite/mobius/Link";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";

const enum fields {
	sectionHeading = "sectionHeading",
	supplierDetails = "supplierDetails",
}

export interface supplierDetailModel {
	fields: {
		caption?: string;
		image: string;
		supplierName?: string;
		linkTitle?: string;
		supplierLink?: LinkFieldValue;
		supplierShortDescription?: string;
		openInNewWindow?: boolean;
	};
}

interface OwnProps extends WidgetProps {
	fields: {
		[fields.sectionHeading]: string;
		[fields.supplierDetails]: supplierDetailModel[];
	};
}


type Props = OwnProps & WidgetProps;


export interface FeaturedSuppliersStyles {
	sectionHead?: InjectableCss;
	supplierWrapper?: InjectableCss;
	supplierLogoStyle?: InjectableCss;
	supplierInfoWrapper?: InjectableCss;
	supplierInfo?: InjectableCss;
	sectionContainer?: InjectableCss;
}

export const featuredSuppliersStyles: FeaturedSuppliersStyles = {
	sectionContainer: {
		css: css`
			margin: 48px 0;
		`
	},
	sectionHead: {
		css: css`
			border-bottom: 1px solid ${WhitecapColors.borderGray};
			text-align: center;
			margin-bottom: 48px;
			padding-bottom: 12px;
			h3{	
				text-transform: uppercase;
				letter-spacing: 1px;
				font-weight: 500;
				font-size: 18px;
			}
		`
	},
	supplierWrapper: {
		css: css`
			div[class^="GridOffset"]{
				justify-content: flex-start;
				align-items: flex-start;
			}
		`
	},
	supplierLogoStyle: {
		css: css`
			width: 100%;
    		text-align: center;
			div[class^="LazyImageStyle"]{
				max-width: 175px;
				max-height: 175px;
			}
			img{
				max-width: 175px;
				max-height: 175px;
			}
		`
	},
	supplierInfoWrapper: {
		css: css`
			flex-direction: column;
			align-items: center;
			justify-content: center;
			${({ theme }: { theme: BaseTheme }) =>
				breakpointMediaQueries(
					theme,
					[
						css`
						padding: 0 15px 10px 15px;
					`,
						css`
						padding: 0 15px 10px 15px;
					`,
						null,
					],
					"max",
				)}
		`
	},
	supplierInfo: {
		css: css`
			padding: 10px 0;
			color: ${WhitecapColors.lightBlack}
			strong{
				font-weight: 600 !important;
				display: block;
				padding-bottom: 10px;
			}
			p, strong{
				font-size: 14px;
				font-weight: 400;
				line-height: 20px;
				text-align: center;
			}
			a{
				color: ${WhitecapColors.orangeLink};
				font-size: 14px;
				font-weight: 400;
				line-height: 20px;
				text-align: center;
				width: 100%;
				margin: 0 auto;
    			display: block;
				padding-bottom: 10px;
				span{
					color: ${WhitecapColors.orangeLink} !important;
				}
			}
		`
	}
};

const styles = featuredSuppliersStyles;



const FeaturedSuppliers: React.FC<Props> = ({ id, fields }: Props) => {
	const GenrateUrl = ({ text, link, openInNewWindow }: { text: string; link: LinkFieldValue, openInNewWindow: boolean }) => {
		const url = useGetLink(link);
		return (
			<StyledWrapper >
				<Link target={openInNewWindow ? "_blank" : "_self"} href={url.url}>
					{text}
				</Link>
			</StyledWrapper>
		);
	};
	return (
		<StyledWrapper {...styles.sectionContainer}>
			<StyledWrapper {...styles.sectionHead}>
				<Typography as="h3">{fields.sectionHeading}</Typography>
			</StyledWrapper>
			<GridContainer {...styles.supplierWrapper}>
				{fields.supplierDetails && fields.supplierDetails.map((item) => (
					<GridItem width={[12, 12, 6, 3, 3]} {...styles.supplierInfoWrapper}>
						{item.fields.image && (
							<StyledWrapper {...styles.supplierLogoStyle} className="supplier-logo">
								<LazyImage src={item.fields.image} />
							</StyledWrapper>
						)}
						<StyledWrapper {...styles.supplierInfo}>
							{item.fields.supplierName && (
								<Typography as="strong">{item.fields.supplierName}</Typography>
							)}
							{item.fields.linkTitle && item.fields.supplierLink && (
								<GenrateUrl openInNewWindow={item?.fields?.openInNewWindow ?? true} text={item?.fields?.linkTitle} link={item.fields.supplierLink} key={item.fields.linkTitle} />
							)}
							{item.fields.supplierShortDescription && (
								<Typography as="p">{item.fields.supplierShortDescription}</Typography>
							)}
						</StyledWrapper>
					</GridItem>
				))}
			</GridContainer>
		</StyledWrapper>

	);
};

const basicTab = {
	displayName: "Basic",
	sortOrder: 0,
};

const widgetModule: WidgetModule = {
	component: FeaturedSuppliers,
	definition: {
		group: "Basic",
		fieldDefinitions: [
			{
				name: fields.sectionHeading,
				displayName: "Section Heading",
				editorTemplate: "TextField",
				defaultValue: "",
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.supplierDetails,
				editorTemplate: "ListField",
				getDisplay: (item: HasFields) => {
					return item.fields.caption;
				},
				defaultValue: [],
				fieldType: "General",
				fieldDefinitions: [
					{
						name: "caption",
						displayName: "Placeholder Caption for Tile",
						editorTemplate: "TextField",
						defaultValue: "",
					},
					{
						name: "supplierName",
						displayName: "Supplier Name",
						editorTemplate: "TextField",
						defaultValue: "",
					},
					{
						name: "image",
						displayName: "Supplier Logo",
						editorTemplate: "ImagePickerField",
						defaultValue: "",
					},
					{
						name: "linkTitle",
						displayName: "Supplier Link Title",
						editorTemplate: "TextField",
						defaultValue: "",
					},
					{
						name: "supplierLink",
						editorTemplate: "LinkField",
						displayName: "Supplier Link",
						defaultValue: {
							value: "",
							type: "Url",
						},
					},
					{
						name: "openInNewWindow",
						displayName: "Open in New Window",
						editorTemplate: "CheckboxField",
						defaultValue: true,
						tab: basicTab,
					},
					{
						name: "supplierShortDescription",
						displayName: "Supplier Short Description",
						editorTemplate: "TextField",
						defaultValue: "",
					},
				],
			},
		],

	}

};

export default widgetModule;